import Vue from 'vue';
import { mapErrorsToInputs } from '@/util/forms';
import { updateArrayItem } from '@/util/array';
import userService from '../../api/user-service';
import i18n from '../../i18n/i18n-config';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';

const getDefaultFormItem = () => ({
  permit_status_change_notification_airports: {},
  permit_issue_notification_airports: {},
  permit_statuses: {},
  permissions: {},
  person: {},
  status: 'active',
  role: 'user',
  is_searchable_as_accompanying_user: false,
  is_guest: false,
});

const getDefaultImportUsersForm = () => ({
  should_send_login_emails: false,
});

export const getDefaultUserFilterParams = () => ({});

const state = {
  users: null,
  activeSubjectAdmins: [],
  userPagination: {},
  newUser: getDefaultFormItem(),
  editedUser: {},
  userValidationErrors: {},
  userFilterParams: getDefaultUserFilterParams(),
  loadingUsers: false,
  savingUser: false,
  disabledUserIds: {},
  importUsersPayload: getDefaultImportUsersForm(),
  exportingUsers: false,
  currentUserIp: null,
  sendingEmail: false,
  emailErrors: {},
};

const getters = {
  permissions() {
    return [
      {
        label: i18n.t('create_subject'),
        value: 'create_subject',
      },
      {
        label: i18n.t('create_temporary_person_permit_planning_events'),
        value: 'create_temporary_person_permit_planning_events',
      },
      {
        label: i18n.t('approve_permanent_vehicle_permit'),
        value: 'approve_permanent_vehicle_permit',
      },
      {
        label: i18n.t('approve_permanent_person_permit'),
        value: 'approve_permanent_person_permit',
      },
      {
        label: i18n.t('approve_temporary_permits_vno'),
        value: 'approve_temporary_permits_vno',
      },
      {
        label: i18n.t('approve_temporary_permits_kun'),
        value: 'approve_temporary_permits_kun',
      },
      {
        label: i18n.t('approve_temporary_permits_plq'),
        value: 'approve_temporary_permits_plq',
      },
      {
        label: i18n.t('check_impeccable_reputation'),
        value: 'check_impeccable_reputation',
      },
      {
        label: i18n.t('approve_parking_info'),
        value: 'approve_parking_info',
      },
      {
        label: i18n.t('is_visible_access_attempts'),
        value: 'is_visible_access_attempts',
      },
      {
        label: i18n.t('create_temporary_permits'),
        value: 'create_temporary_permits',
      },
      {
        label: i18n.t('create_multiple_temporary_permits'),
        value: 'create_multiple_temporary_permits',
      },
      {
        label: i18n.t('show_print_log'),
        value: 'show_print_log',
      },
      {
        label: i18n.t('driving_training_instructor'),
        value: 'driving_training_instructor',
      },
      {
        label: i18n.t('driving_permit_admin'),
        value: 'driving_permit_admin',
      },
      {
        label: i18n.t('approve_subject_driving_permit'),
        value: 'approve_subject_driving_permit',
      },
      {
        label: i18n.t('view_locations_module'),
        value: 'view_locations',
      },
      {
        label: i18n.t('edit_permit_issued_permanent_person_end_date'),
        value: 'edit_permit_issued_permanent_person_end_date'
      },
      {
        label: i18n.t('allow_to_give_permission_to_only_select_kun_k_zone'),
        value: 'allow_to_give_permission_to_only_select_kun_k_zone'
      },
      {
        label: i18n.t('limit_to_select_only_kun_airport_and_k_zone'),
        value: 'limit_to_select_only_kun_airport_and_k_zone'
      },
      {
        label: i18n.t('mark_own_subject_permit_not_valid'),
        value: 'mark_own_subject_permit_not_valid',
      },
      {
        label: i18n.t('see_subject_admins_who_is_allowed_to_approve_driving_permit'),
        value: 'see_subject_admins_who_is_allowed_to_approve_driving_permit'
      }
    ];
  },

  roles() {
    return [
      { value: 'admin', text: i18n.t('user_roles.admin') },
      { value: 'subject_admin', text: i18n.t('user_roles.subject_admin') },
      { value: 'user', text: i18n.t('user_roles.user') },
      { value: 'ltou_admin', text: i18n.t('user_roles.ltou_admin') },
      { value: 'ltou_employee', text: i18n.t('user_roles.ltou_employee') },
      {
        value: 'impeccable_reputation_institution_employee',
        text: i18n.t('user_roles.impeccable_reputation_institution_employee'),
      },
    ];
  },

  validatedRoles(state, getters, rootState, rootGetters) {
    const roles = [];
    if (!rootGetters['auth/currentUser']) {
      return [];
    }
    if (rootGetters['auth/currentUser'].role === 'admin') {
      roles.push(
        { value: 'admin', text: i18n.t('user_roles.admin') },
        { value: 'subject_admin', text: i18n.t('user_roles.subject_admin') },
        { value: 'user', text: i18n.t('user_roles.user') },
        { value: 'ltou_admin', text: i18n.t('user_roles.ltou_admin') },
        { value: 'ltou_employee', text: i18n.t('user_roles.ltou_employee') },
        {
          value: 'impeccable_reputation_institution_employee',
          text: i18n.t('user_roles.impeccable_reputation_institution_employee'),
        },
      );
    }
    if (rootGetters['auth/currentUser'].role === 'ltou_admin') {
      roles.push(
        { value: 'subject_admin', text: i18n.t('user_roles.subject_admin') },
        { value: 'user', text: i18n.t('user_roles.user') },
        { value: 'ltou_admin', text: i18n.t('user_roles.ltou_admin') },
        { value: 'ltou_employee', text: i18n.t('user_roles.ltou_employee') },
        {
          value: 'impeccable_reputation_institution_employee',
          text: i18n.t('user_roles.impeccable_reputation_institution_employee'),
        },
      );
    }
    if (rootGetters['auth/currentUser'].role === 'ltou_employee') {
      roles.push(
        { value: 'subject_admin', text: i18n.t('user_roles.subject_admin') },
        { value: 'user', text: i18n.t('user_roles.user') },
      );
    }
    if (rootGetters['auth/currentUser'].role === 'subject_admin') {
      roles.push({ value: 'user', text: i18n.t('user_roles.user') });
    }
    return roles;
  },

  statuses() {
    return [
      { value: 'active', text: i18n.t('user_statuses.active') },
      { value: 'suspended', text: i18n.t('user_statuses.suspended') },
    ];
  },

  documentTypes() {
    return [
      { value: 'id_card', text: i18n.t('document_types.id_card') },
      { value: 'passport', text: i18n.t('document_types.passport') },
      { value: 'residence_permit', text: i18n.t('document_types.residence_permit') },
    ];
  },
};

const mutations = {
  SET_USERS(state, { data, current_page, per_page, total }) {
    state.users = data;
    state.userPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_EXPORTING_USERS(state, exporting) {
    state.exportingUsers = exporting
  },

  SET_ACTIVE_SUBJECT_ADMINS(state, list) {
    state.activeSubjectAdmins = Array.isArray(list) ? list : []
  },

  SET_FILTER_PARAMS(state, params) {
    state.userFilterParams = params;
  },

  SET_EDITED_USER(state, user) {
    state.userValidationErrors = {};
    state.editedUser = JSON.parse(JSON.stringify(user));

    // transforming permissions in user edit form
    const transformedPermissions = {};
    if (user.permissions) {
      user.permissions.forEach((p) => {
        transformedPermissions[p.permission] = true;
      });
    }
    state.editedUser.permissions = transformedPermissions;

    // transforming airport notification airports in user edit form
    const transformedNotificationAirports = {};
    if (user.permit_status_change_notification_airports) {
      user.permit_status_change_notification_airports.forEach((a) => {
        transformedNotificationAirports[a.id] = true;
      });
    }
    state.editedUser.permit_status_change_notification_airports = transformedNotificationAirports;

    // transforming airport issue notification airports in user edit form
    const transformedIssueNotificationAirports = {};
    if (user.permit_issue_notification_airports) {
      user.permit_issue_notification_airports.forEach((a) => {
        transformedIssueNotificationAirports[a.id] = true;
      });
    }
    state.editedUser.permit_issue_notification_airports = transformedIssueNotificationAirports;

    // transforming statuses in user edit form
    const transformedStatuses = {};
    if (user.permit_statuses) {
      user.permit_statuses.forEach((s) => {
        transformedStatuses[s.permit_status] = true;
      });
    }
    state.editedUser.permit_statuses = transformedStatuses;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.userValidationErrors[field];
  },

  STORE_USER(state, user) {
    state.users = [...state.users, user];
    state.newUser = getDefaultFormItem();
    state.userValidationErrors = {};
    state.userPagination.total += 1;
  },

  UPDATE_USER(state, user) {
    state.users = updateArrayItem(state.users, user);
  },

  SET_USER_VALIDATION_ERRORS(state, userValidationErrors) {
    state.userValidationErrors = userValidationErrors;
  },

  RESET_USER_VALIDATION_ERRORS(state) {
    state.userValidationErrors = {};
  },

  SET_LOADING_USERS(state, loading) {
    state.loadingUsers = loading;
  },

  SET_SAVING_USER(state, saving) {
    state.savingUser = saving;
  },

  TOGGLE_DISABLED_USER(state, id) {
    if (state.disabledUserIds[id]) {
      Vue.delete(state.disabledUserIds, id);
    } else {
      Vue.set(state.disabledUserIds, id, true);
    }
  },

  SET_CURRENT_USER_IP(state, ip) {
    state.currentUserIp = ip;
  },

  SET_SENDING_EMAIL(state, sending) {
    state.sendingEmail = !!sending;
  },

  SET_EMAIL_VALIDATION_ERRORS(state, emailErrors) {
    state.emailErrors = emailErrors;
  },

  RESET_EMAIL_VALIDATION_ERRORS(state) {
    state.emailErrors = {};
  },

  CLEAR_EMAIL_VALIDATION_ERRORS(state, field) {
    delete state.emailErrors[field];
  },
};

const actions = {
  fetchUsers({ state, commit }, params) {
    commit('SET_LOADING_USERS', true);
    commit('SET_FILTER_PARAMS', params);
    return userService
      .getPage(params)
      .then((res) => {
        commit('SET_USERS', res.data.all);
        commit('SET_ACTIVE_SUBJECT_ADMINS', res.data.active_subject_admins || [])
        if (params.page !== state.userFilterParams.page) {
          window.scrollTo(0, 0);
        }
      })
      .finally(() => {
        commit('SET_LOADING_USERS', false);
      });
  },

  editUser({ state, commit }, userId) {
    const user = state.users?.find((u) => u.id === userId);
    if (user) {
      commit('SET_EDITED_USER', user);
      return Promise.resolve(user);
    }
    return userService.getById(userId).then((res) => {
      commit('SET_EDITED_USER', res.data);
    });
  },

  clearUserValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },

  clearEmailValidationErrors({ commit }, field) {
    commit('CLEAR_EMAIL_VALIDATION_ERRORS', field);
  },

  storeUser({ commit }, user) {
    commit('SET_SAVING_USER', true);
    return userService
      .create(user)
      .then((res) => {
        commit('STORE_USER', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_created'));
      })
      .catch((err) => {
        commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => {
        commit('SET_SAVING_USER', false);
      });
  },

  updateUser({ commit }, user) {
    commit('SET_SAVING_USER', true);
    return userService
      .update(user)
      .then((res) => {
        commit('UPDATE_USER', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_updated'));
      })
      .catch((err) => {
        commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => {
        commit('SET_SAVING_USER', false);
      });
  },

  resetPassword({ commit }, user) {
    openConfirmDialog({
      title: i18n.t('confirm_password_reset'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      commit('TOGGLE_DISABLED_USER', user.id);
      userService
        .resetPassword(user)
        .then(() => {
          commit('UPDATE_USER', {
            ...user,
            status: 'active',
          });
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('password_was_reset'));
        })
        .finally(() => {
          commit('TOGGLE_DISABLED_USER', user.id);
        });
    });
  },

  async toggleUserStatus({ commit }, user) {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    const msg = i18n.t(`user_${newStatus === 'active' ? 'activated' : 'suspended'}`);
    commit('TOGGLE_DISABLED_USER', user.id);
    try {
      await userService.toggleStatus(user);
      commit('UPDATE_USER', {
        ...user,
        status: newStatus,
      });
      eventBus.$emit(OPEN_SNACKBAR, msg);
    } catch (e) {
      eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_status_toggle_failed'));
    }
    commit('TOGGLE_DISABLED_USER', user.id);
  },

  importUsers({ commit }, { importPayload, hideMessage = false }) {
    return userService
      .importUsers(importPayload)
      .then((res) => {
        if (!hideMessage) {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('users_imported_successfully'));
        }
        return res;
      })
      .catch((err) => {
        commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  exportUsersXlsx({ commit }, params) {
    commit('SET_EXPORTING_USERS', true)

    return userService
      .exportUsersXlsx(params)
      .then((res) => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'naudotojai.xlsx'
        link.click()
        window.URL.revokeObjectURL(link.href)
      })
      .finally(() => {
        commit('SET_EXPORTING_USERS', false)
      })
  },

  async suspendUsers({ commit }, ids) {
    const confirmed = await openConfirmDialog({
      title: i18n.t(
        ids.length ? 'confirm_users_suspend' : 'confirm_continue_without_users_suspend',
      ),
      confirmText: i18n.t(ids.length ? 'suspend_users' : 'continue_without_suspend'),
      maxWidth: ids.length ? 740 : 420,
    });
    if (!confirmed) {
      return false;
    }
    // eslint-disable-next-line consistent-return
    await userService.suspendUsers(ids).catch((err) => {
      commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
      throw err;
    });
    eventBus.$emit(OPEN_SNACKBAR, i18n.t(ids.length ? 'users_suspended' : 'users_confirmed'));
    return true;
  },

  updatePerson({ commit }, person) {
    return userService
      .updatePerson(person)
      .then(() => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_updated'));
        return true;
      })
      .catch((err) => {
        commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  getCurrentUserIp({ commit }) {
    return userService
      .getCurrentUserIp()
      .then((res) => {
        commit('SET_CURRENT_USER_IP', res.data.ip);
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  },

  sendEmailActiveSubjectAdmins({ commit }, { subject, message, attachments }) {
    commit('SET_SENDING_EMAIL', true);
    commit('RESET_EMAIL_VALIDATION_ERRORS');
    
    return userService
      .sendEmailActiveSubjectAdmins({ subject, message, attachments: attachments || [] })
      .then((res) => {
        const success = res.data?.success;
        const msgKey = success ? 'email_send_success' : 'email_send_failed';
        eventBus.$emit(OPEN_SNACKBAR, i18n.t(msgKey));
        return res.data;
      })
      .catch((err) => {
        if (err?.response?.status === 422) {
          commit('SET_EMAIL_VALIDATION_ERRORS', mapErrorsToInputs(err));
        } else {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('email_send_failed'));
        }
        throw err;
      })
      .finally(() => {
        commit('SET_SENDING_EMAIL', false);
      });
  },

  updateUserProfile({ commit }, person) {
    return userService
      .updateUserProfile(person)
      .then(() => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('user_updated'));
        return true;
      })
      .catch((err) => {
        commit('SET_USER_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
